import { useState, useCallback } from 'react';
import axios from 'axios';
import { CulturalEvent } from '../constants/culturalEventsTypes';

// Hardcode to production API - same as admin panel
const API_BASE_URL = 'https://cultural-events-api-80b5fb86b6c5.herokuapp.com/api';

// Determine API URL at runtime - ALWAYS use production
const getAPIBaseURL = (): string => {
  // Always use production API for cultural events
  console.log('🔗 Using production Cultural Events API:', API_BASE_URL);
  return API_BASE_URL;
};

export const useCulturalEventsAPI = () => {
  const [events, setEvents] = useState<CulturalEvent[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const getPublicEvents = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const apiUrl = getAPIBaseURL();
      const endpoint = `${apiUrl}/cultural-events`;
      console.log('🎭 Fetching public events from:', endpoint);
      console.log('🌐 Current location:', typeof window !== 'undefined' ? window.location.href : 'server');
      const response = await axios.get(endpoint, {
        timeout: 15000,
        headers: { 
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      console.log('✅ Events fetched:', response.data);
      const eventsData = response.data.events || (Array.isArray(response.data) ? response.data : []);
      setEvents(Array.isArray(eventsData) ? eventsData : []);
      return response.data;
    } catch (err: any) {
      console.error('❌ Error fetching events:', err);
      console.error('❌ Error details:', {
        message: err.message,
        code: err.code,
        response: err.response?.data,
        status: err.response?.status,
        url: err.config?.url
      });
      const errorMessage = err.response?.data?.message || err.message || 'Network Error';
      setError(errorMessage);
      setEvents([]);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const getUpcomingEvents = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Re-check API URL at runtime to ensure we're using the right one
      const apiUrl = getAPIBaseURL();
      const endpoint = `${apiUrl}/cultural-events/upcoming`;
      
      console.log('🎭 Fetching upcoming events from:', endpoint);
      console.log('🌐 Current location:', typeof window !== 'undefined' ? window.location.href : 'server');
      
      const response = await axios.get(endpoint, {
        timeout: 20000, // 20 second timeout for mobile networks
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        // Ensure CORS credentials are handled
        withCredentials: false
      });
      
      console.log('✅ Upcoming events fetched:', response.data);
      console.log('📦 Response structure:', {
        hasEvents: !!response.data.events,
        isArray: Array.isArray(response.data),
        eventsType: typeof response.data.events,
        dataKeys: Object.keys(response.data || {}),
        count: response.data.count,
        success: response.data.success
      });
      
      // Handle different response structures
      let eventsArray: CulturalEvent[] = [];
      if (response.data.events && Array.isArray(response.data.events)) {
        eventsArray = response.data.events;
        console.log('✅ Found events in response.data.events');
      } else if (Array.isArray(response.data)) {
        eventsArray = response.data;
        console.log('✅ Found events as direct array');
      } else {
        console.warn('⚠️ No events found in response. Response structure:', response.data);
        eventsArray = [];
      }
      
      console.log('📊 Parsed events count:', eventsArray.length);
      if (eventsArray.length > 0) {
        console.log('📋 Event IDs:', eventsArray.map(e => e._id));
        console.log('📅 Event dates:', eventsArray.map(e => ({ title: e.title, date: e.date })));
      } else {
        console.warn('⚠️ No events parsed. Full response:', JSON.stringify(response.data, null, 2));
      }
      setEvents(eventsArray);
      return response.data;
    } catch (err: any) {
      console.error('❌ Error fetching upcoming events:', err);
      console.error('❌ Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        url: err.config?.url
      });
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch upcoming events';
      setError(errorMessage);
      setEvents([]); // Clear events on error
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const getEventById = useCallback(async (id: string) => {
    try {
      setLoading(true);
      setError(null);
      const apiUrl = getAPIBaseURL();
      console.log('🎭 Fetching event by ID:', id, 'from:', `${apiUrl}/cultural-events/${id}`);
      const response = await axios.get(`${apiUrl}/cultural-events/${id}`, {
        timeout: 10000,
        headers: { 'Accept': 'application/json' }
      });
      console.log('✅ Event fetched:', response.data);
      return response.data;
    } catch (err: any) {
      console.error('❌ Error fetching event:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch event';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const getEventsByType = useCallback(async (type: string) => {
    try {
      setLoading(true);
      setError(null);
      const apiUrl = getAPIBaseURL();
      console.log('🎭 Fetching events by type:', type, 'from:', `${apiUrl}/cultural-events/type/${type}`);
      const response = await axios.get(`${apiUrl}/cultural-events/type/${type}`, {
        timeout: 10000,
        headers: { 'Accept': 'application/json' }
      });
      console.log('✅ Events by type fetched:', response.data);
      const eventsData = response.data.events || (Array.isArray(response.data) ? response.data : []);
      setEvents(Array.isArray(eventsData) ? eventsData : []);
      return response.data;
    } catch (err: any) {
      console.error('❌ Error fetching events by type:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch events by type';
      setError(errorMessage);
      setEvents([]);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    events,
    loading,
    error,
    getPublicEvents,
    getUpcomingEvents,
    getEventById,
    getEventsByType,
    refetch: getPublicEvents,
  };
};
